import { useState, useEffect } from "react";
import { fetchCompanyChildAction } from "../../actions/fetchCompanyChildAction";

export const initialParentForm = {
  parentCode: "",
  parentName: "",
  description: "",
  status: "active",
};

export const initialChildForm = {
  childCode: "",
  childName: "",
  parentCode: "",
  description: "",
  status: "active",
};

/**
 * API shape: [{ id, parentName, children: [{ id, parentName }] }]
 * Sidebar = top-level records (parents)
 * Cards   = children array inside each parent
 */
const mapApiData = (records) => {
  const parents = [];
  const allChildren = [];

  records.forEach((record) => {
    const parentCode = String(record.id);
    const parentName = record.parentName?.trim() || "No Parents";
    parents.push({
      parentCode,
      parentName,
      description: "",
      status: "active",
    });

    (record.children || []).forEach((child) => {
      allChildren.push({
        childCode: String(child.id),
        childName: child.parentName?.trim() || "—",
        parentCode,
        parentName,
        entityId: child.entityId || "",
        description: "",
        status: "active",
      });
    });
  });

  return { parents, allChildren };
};

export const useParentCodeData = () => {
  const [parents, setParents] = useState([]);
  const [allChildren, setAllChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedParent, setSelectedParent] = useState(null);

  // ── Fetch from API on mount ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const result = await fetchCompanyChildAction();
      if (result.success) {
        const { parents: p, allChildren: c } = mapApiData(result.data);
        setParents(p);
        setAllChildren(c);
      }
      setIsLoading(false);
    };
    load();
  }, []);

  // ── Filtered children by selected parent ──────────────────────────────────
  const children = selectedParent
    ? allChildren.filter((c) => c.parentCode === selectedParent)
    : allChildren;

  // ── Parent modal ───────────────────────────────────────────────────────────
  const [parentModal, setParentModal] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  const [parentForm, setParentForm] = useState(initialParentForm);
  const [parentErrors, setParentErrors] = useState({});

  const openParentModal = () => {
    setEditingParent(null);
    setParentForm(initialParentForm);
    setParentErrors({});
    setParentModal(true);
  };

  const openEditParentModal = (parent) => {
    setEditingParent(parent.parentCode);
    setParentForm({ ...parent });
    setParentErrors({});
    setParentModal(true);
  };

  const closeParentModal = () => setParentModal(false);

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentForm((prev) => ({ ...prev, [name]: value }));
    if (parentErrors[name])
      setParentErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const validateParent = () => {
    const errs = {};
    if (!parentForm.parentCode.trim()) errs.parentCode = "Code is required";
    if (!parentForm.parentName.trim()) errs.parentName = "Name is required";
    return errs;
  };

  const submitParent = () => {
    const errs = validateParent();
    if (Object.keys(errs).length) {
      setParentErrors(errs);
      return;
    }
    if (editingParent) {
      setParents((prev) =>
        prev.map((p) =>
          p.parentCode === editingParent ? { ...parentForm } : p,
        ),
      );
    } else {
      setParents((prev) => [...prev, { ...parentForm }]);
    }
    closeParentModal();
  };

  // ── Child modal ────────────────────────────────────────────────────────────
  const [childModal, setChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [childForm, setChildForm] = useState(initialChildForm);
  const [childErrors, setChildErrors] = useState({});

  const openChildModal = () => {
    setEditingChild(null);
    setChildForm(initialChildForm);
    setChildErrors({});
    setChildModal(true);
  };

  const openEditChildModal = (child) => {
    setEditingChild(child.childCode);
    setChildForm({ ...child });
    setChildErrors({});
    setChildModal(true);
  };

  const closeChildModal = () => setChildModal(false);

  const handleChildChange = (e) => {
    const { name, value } = e.target;
    setChildForm((prev) => ({ ...prev, [name]: value }));
    if (childErrors[name])
      setChildErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const validateChild = () => {
    const errs = {};
    if (!childForm.parentCode) errs.parentCode = "Parent is required";
    if (!childForm.childCode.trim()) errs.childCode = "Code is required";
    if (!childForm.childName.trim()) errs.childName = "Name is required";
    return errs;
  };

  const submitChild = () => {
    const errs = validateChild();
    if (Object.keys(errs).length) {
      setChildErrors(errs);
      return;
    }
    if (editingChild) {
      setAllChildren((prev) =>
        prev.map((c) => (c.childCode === editingChild ? { ...childForm } : c)),
      );
    } else {
      setAllChildren((prev) => [...prev, { ...childForm }]);
    }
    closeChildModal();
  };

  return {
    parents,
    children,
    allChildren,
    isLoading,
    selectedParent,
    setSelectedParent,
    parentModal,
    editingParent,
    openParentModal,
    openEditParentModal,
    closeParentModal,
    parentForm,
    parentErrors,
    handleParentChange,
    submitParent,
    childModal,
    editingChild,
    openChildModal,
    openEditChildModal,
    closeChildModal,
    childForm,
    childErrors,
    handleChildChange,
    submitChild,
  };
};
